/**
 * The people this client has not met yet (docs/fast-start.md D1056–D1058) — the
 * web's `state::Wanted`.
 *
 * The directory holds whoever an answer named. A read that finds nobody there
 * notes the id here (`mutators.nameOf`, `mutators.met`) and the runtime asks the
 * gateway a moment later, so every drawing path is covered by being a read of
 * the directory, with no list of call sites to keep.
 *
 * Module state and not the store: those reads happen inside a render or a recipe,
 * neither of which may publish anything, and what is noted is about this
 * session's requests and nothing the window draws.
 */

/** Noted and not asked about yet. */
const wanted = new Set<number>();
/** Asked about, answered or not: an account that is gone is not asked about on
 * every read of its name for the rest of the session. */
const asked = new Set<number>();
/** When each search was last asked, by the words as `queryKey` folds them. */
const queries = new Map<string, number>();
/** The most searches remembered: past it the oldest are as good as never asked. */
const MAX_QUERIES = 256;
/** How long a search stands answered (D1058). */
const QUERY_TTL_MS = 30_000;
/** How long the Pets tab's read stands (D405, D1058): a pet made since reaches the
 * tab no later than this, no frame naming it to a client that has not met it. */
const PETS_TTL_MS = 300_000;
/** When the Pets tab's read last went, or null. */
let petsAskedAt: number | null = null;
let onWant: (() => void) | null = null;

/** Who to tell when an id is noted. One listener: the runtime, which is told at
 * once of whatever was noted before it was there. Answers how to stop telling it. */
export function onWanted(listener: () => void): () => void {
  onWant = listener;
  if (wanted.size > 0) listener();
  return () => {
    if (onWant === listener) onWant = null;
  };
}

/** Notes an account a read found nobody for. Id 0 is never anybody (D841). Only
 * sets a timer on the listener's side: this runs inside renders and recipes. */
export function want(userId: number): void {
  if (userId === 0 || wanted.has(userId) || asked.has(userId)) return;
  wanted.add(userId);
  onWant?.();
}

export function hasWanted(): boolean {
  return wanted.size > 0;
}

/** Up to `max` ids to ask about now, marked as asked. */
export function takeWanted(max: number): number[] {
  const ids: number[] = [];
  for (const id of wanted) {
    if (ids.length === max) break;
    ids.push(id);
  }
  for (const id of ids) {
    wanted.delete(id);
    asked.add(id);
  }
  return ids;
}

/** This id is being asked about apart from the batches, and they leave it be. */
export function askApart(userId: number): void {
  wanted.delete(userId);
  asked.add(userId);
}

/** The gateway could not be asked: these wait to be asked again. */
export function askAgain(ids: readonly number[]): void {
  for (const id of ids) {
    asked.delete(id);
    wanted.add(id);
  }
}

/** The form a search is remembered in: the same words in another case are the
 * same question. */
function queryKey(query: string): string {
  return query.trim().toLowerCase();
}

/** Whether this search was asked less than `QUERY_TTL_MS` ago. */
export function queryAnswered(query: string, nowMs: number): boolean {
  const at = queries.get(queryKey(query));
  return at !== undefined && nowMs - at < QUERY_TTL_MS;
}

/** This search is being asked now. */
export function noteQuery(query: string, nowMs: number): void {
  if (queries.size >= MAX_QUERIES) queries.clear();
  queries.set(queryKey(query), nowMs);
}

/** The gateway could not be asked this search: the next time it is typed it asks. */
export function queryAgain(query: string): void {
  queries.delete(queryKey(query));
}

/** Whether the live pets are to be asked for now, marked as asked. */
export function takePets(nowMs: number): boolean {
  if (petsAskedAt !== null && nowMs - petsAskedAt < PETS_TTL_MS) return false;
  petsAskedAt = nowMs;
  return true;
}

/** The gateway could not be asked for the live pets: the next read asks. */
export function petsAgain(): void {
  petsAskedAt = null;
}

/** The account ended, and whoever it wanted with it. The listener stays. */
export function forgetWanted(): void {
  wanted.clear();
  asked.clear();
  queries.clear();
  petsAskedAt = null;
}
